import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wallet,
  TrendingUp,
  Lock,
  Hourglass,
  BarChart3,
  ArrowRight,
  ArrowLeft,
  Receipt,
  ShieldCheck,
  Banknote,
  CalendarCheck,
  CircleHelp,
  Landmark,
  Send,
  Ticket,
} from "lucide-react";
import { formatRupiah, Spinner, Alert } from "../../components/ui";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cacheGet, cacheSet, cacheKey } from "../../lib/cache";
import UsageTour, { TourReplayButton } from "../../components/UsageTour";

const FINANCE_TOUR = [
  {
    title: "Keuanganmu, dijelasin simpel 💰",
    text: "Banyak angka di sini — tur kilat 30 detik biar ngerti semua tombol.",
  },
  {
    selector: "fin-total-released",
    title: "1. Yang sudah cair",
    text: "Uang yang SUDAH jadi milikmu dari job selesai. Angka rata-rata per job membantumu pasang harga.",
  },
  {
    selector: "fin-chart-card",
    title: "2. Grafik 6 bulan",
    text: "Lihat tren pemasukanmu. Naik terus = menuju tier ELITE/PRO (yang masuk kantongmu makin besar).",
  },
  {
    selector: "fin-wallet",
    title: "3. Tarik dana di sini",
    text: "Isi nominal (min Rp50rb) + nomor rekening → Admin transfer. Statusnya terpantau di Riwayat Pencairan bawah.",
  },
  {
    selector: "fin-fee-promo",
    title: "4. Klaim promo potongan admin",
    text: "Punya kode promo? Klaim di sini — potongan admin pada payment BERIKUTNYA otomatis berkurang. Gratisan, jangan sampai hangus!",
  },
  {
    selector: "fin-recent",
    title: "5. Riwayat pencairan",
    text: "Semua pencairan tercatat di sini, klik untuk lihat detail pesanannya. Selesai — semangat nguli! 🎉",
  },
];
import BackButton from "../../components/BackButton";

export default function WorkerFinance() {
  const { user } = useAuth();
  const [data, setData] = useState(() => cacheGet(cacheKey(user?.id, "finance-earnings")).data);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(() => data === null);
  const [wallet, setWallet] = useState(() => cacheGet(cacheKey(user?.id, "finance-wallet")).data);
  const [payouts, setPayouts] = useState(() => cacheGet(cacheKey(user?.id, "finance-payouts")).data || []);
  const [amount, setAmount] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [payoutMsg, setPayoutMsg] = useState("");
  const [payoutErr, setPayoutErr] = useState("");
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [feeCode, setFeeCode] = useState("");
  const [feeMsg, setFeeMsg] = useState("");
  const [feeErr, setFeeErr] = useState("");
  const [feeLoading, setFeeLoading] = useState(false);
  const [feePromos, setFeePromos] = useState([]);

  const fetchFeePromos = async () => {
    try {
      const { data } = await api.get("/api/promos/my-fee-promos");
      setFeePromos(data || []);
    } catch (e) {
      // abaikan: endpoint baru, anggap belum ada klaim
    }
  };

  useEffect(() => {
    Promise.all([
      api.get("/api/payments/worker/earnings"),
      api.get("/api/finance/wallet").catch(() => ({ data: null })),
      api.get("/api/finance/payouts").catch(() => ({ data: [] })),
    ])
      .then(([earn, wal, pay]) => {
        setData(earn.data);
        setWallet(wal.data);
        setPayouts(pay.data || []);
        cacheSet(cacheKey(user?.id, "finance-earnings"), earn.data);
        cacheSet(cacheKey(user?.id, "finance-wallet"), wal.data);
        cacheSet(cacheKey(user?.id, "finance-payouts"), pay.data || []);
      })
      .catch((e) => {
        if (!data) setError(formatApiError(e?.response?.data?.detail));
      })
      .finally(() => setLoading(false));
    fetchFeePromos();
  }, [user?.id]);

  const handleClaimFee = async (e) => {
    e.preventDefault();
    setFeeMsg("");
    setFeeErr("");
    if (!feeCode.trim()) return;
    setFeeLoading(true);
    try {
      const { data } = await api.post("/api/promos/claim", { code: feeCode });
      setFeeCode("");
      setFeeMsg(`Promo ${data.code} diklaim. Otomatis memotong potongan admin pada payment berikutnya.`);
      fetchFeePromos();
    } catch (err) {
      setFeeErr(formatApiError(err.response?.data?.detail));
    } finally {
      setFeeLoading(false);
    }
  };

  const handlePayout = async (e) => {
    e.preventDefault();
    setPayoutMsg("");
    setPayoutErr("");
    setPayoutLoading(true);
    try {
      const { data: p } = await api.post("/api/finance/payouts", {
        amount: parseInt(amount, 10),
        bank_account: bankAccount,
      });
      setPayouts((prev) => [p, ...prev]);
      cacheSet(cacheKey(user?.id, "finance-payouts"), [p, ...payouts]);
      if (wallet) {
        const w2 = { ...wallet, balance: (wallet.balance || 0) - parseInt(amount, 10) };
        setWallet(w2);
        cacheSet(cacheKey(user?.id, "finance-wallet"), w2);
      }
      setAmount("");
      setPayoutMsg("Pengajuan terkirim. Admin akan memproses ke rekeningmu.");
    } catch (err) {
      setPayoutErr(formatApiError(err?.response?.data?.detail));
    } finally {
      setPayoutLoading(false);
    }
  };

  if (loading) return <Spinner label="Memuat keuangan..." />;
  if (error) return <Alert kind="error">{error}</Alert>;
  if (!data) return null;

  const s = data.summary || {};
  const monthly = data.monthly || [];
  const recent = data.recent || [];
  const trend =
    s.last_month > 0
      ? Math.round(((s.this_month - s.last_month) / s.last_month) * 100)
      : null;

  return (
    <div className="animate-fade-up py-8 grid place-items-center" data-testid="worker-finance">
      {!loading && <UsageTour tourKey="tour-finance-v1" steps={FINANCE_TOUR} />}
      <div className="container mx-auto space-y-4 md:space-y-6">
        <div className="flex items-center justify-between">
          <BackButton to="/worker" label="Beranda" />
          <TourReplayButton tourKey="tour-finance-v1" dark={false} />
        </div>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-extrabold text-brand-800">Keuangan</h1>
        <p className="text-brand-400 text-sm mt-0.5">
          Semua uang dari jasamu, dijelasin simpel.
        </p>
      </div>

      {/* Hero: uang yang sudah jadi milikmu */}
      <div
        className="rounded-3xl bg-success-600 text-white p-5 md:p-6"
        data-testid="fin-total-released"
      >
        <div className="flex items-center gap-2 text-success-100">
          <Wallet className="h-4 w-4" />
          <p className="text-sm font-semibold">Sudah cair — aman dipakai</p>
        </div>
        <p className="text-3xl md:text-4xl font-extrabold mt-2 break-words">
          {formatRupiah(s.total_released)}
        </p>
        <p className="text-sm text-success-100 mt-1">
          Dari {s.job_count ?? 0} pekerjaan selesai • rata-rata {formatRupiah(s.avg_per_job)} per job
        </p>
      </div>

      {/* 3 kartu status: 2 kolom di HP biar ringkas */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        <MiniCard
          icon={Lock}
          bg="bg-blue-50"
          fg="text-blue-600"
          label="Ditahan aman"
          value={formatRupiah(s.in_escrow)}
          hint="Cair otomatis sesuai tahap"
          testid="fin-escrow"
        />
        <MiniCard
          icon={Hourglass}
          bg="bg-amber-50"
          fg="text-amber-600"
          label="Nunggu dibayar"
          value={formatRupiah(s.pending_customer)}
          hint="Pelanggan belum bayar"
          testid="fin-pending"
        />
        <div className="col-span-2 lg:col-span-1">
          <MiniCard
            icon={TrendingUp}
            bg="bg-brand-50"
            fg="text-brand-600"
            label="Bulan ini"
            value={formatRupiah(s.this_month)}
            hint={
              trend === null
                ? s.this_month > 0
                  ? "Pencairan pertama 🎉"
                  : "Belum ada pencairan"
                : `${trend >= 0 ? "Naik" : "Turun"} ${Math.abs(trend)}% vs bulan lalu`
            }
            testid="fin-this-month"
          />
        </div>
      </div>

      {/* CTA kalau ada yang perlu perhatian */}
      {(s.pending_customer > 0 || s.in_escrow > 0) && (
        <Link
          to="/worker/bookings"
          className="card p-4 flex items-center gap-3 border-brand-200 bg-brand-50 hover:border-brand-300 transition"
        >
          <CalendarCheck className="h-8 w-8 text-brand-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-brand-800 text-sm">Cek pesananmu</p>
            <p className="text-xs text-brand-500">
              {s.pending_customer > 0
                ? "Ada pesanan yang belum dibayar pelanggan. "
                : ""}
              Pantau status tiap pesanan sampai dana cair.
            </p>
          </div>
          <ArrowRight className="h-5 w-5 text-brand-400 flex-shrink-0" />
        </Link>
      )}

      {/* Promo potongan fee */}
      <div className="card p-5 md:p-6" data-testid="fin-fee-promo">
        <h2 className="font-bold text-brand-800 flex items-center gap-2">
          <Ticket className="h-5 w-5 text-brand-500" /> Promo Potongan Admin
        </h2>
        <p className="text-sm text-brand-400 mt-0.5">
          Klaim kode promo khusus pekerja. Potongan admin otomatis dipakai pada payment berikutnya.
        </p>
        <form onSubmit={handleClaimFee} className="mt-3 flex gap-2">
          <input
            type="text"
            value={feeCode}
            onChange={(e) => setFeeCode(e.target.value.toUpperCase())}
            placeholder="cth. FEEPRO10"
            className="flex-1 rounded-xl border-2 border-brand-100 px-3 py-2 text-sm uppercase focus:border-brand-500 focus:outline-none"
            data-testid="fee-promo-code"
          />
          <button type="submit" className="btn-primary" disabled={feeLoading || !feeCode.trim()} data-testid="fee-promo-claim">
            {feeLoading ? "Klaim..." : "Klaim"}
          </button>
        </form>
        {feeErr && <p className="text-sm text-red-600 mt-2">{feeErr}</p>}
        {feeMsg && <p className="text-sm text-success-600 mt-2">{feeMsg}</p>}
        {feePromos.length > 0 && (
          <ul className="divide-y divide-brand-50 mt-3">
            {feePromos.map((f, i) => (
              <li key={i} className="flex items-center justify-between py-2 gap-3">
                <p className="text-sm font-bold text-brand-800">
                  {f.code}
                  <span className="ml-2 text-xs font-semibold text-brand-500">
                    {f.discount_type === "PERCENTAGE" ? `${f.discount_value}%` : formatRupiah(f.discount_value)}
                  </span>
                </p>
                <span className={`chip flex-shrink-0 ${f.applied ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                  {f.applied ? "Terpakai" : "Siap dipakai"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Alur dana: biar nggak bingung */}
      <div className="card p-5 md:p-6">
        <h2 className="font-bold text-brand-800 flex items-center gap-2">
          <CircleHelp className="h-5 w-5 text-brand-500" /> Ke mana perginya uangku?
        </h2>
        <ol className="mt-4 space-y-0">
          <FlowStep
            n={1}
            icon={Banknote}
            title="Pelanggan bayar"
            desc="Uang masuk dan langsung ditahan sistem, bukan ke siapa-siapa dulu."
            last={false}
          />
          <FlowStep
            n={2}
            icon={ShieldCheck}
            title="Duit ditahan aman (escrow)"
            desc="Selama kerja, uang aman. Kalau ada masalah, bisa komplain."
            last={false}
          />
          <FlowStep
            n={3}
            icon={Wallet}
            title="30% cair saat dikonfirmasi"
            desc="Begitu pelanggan (atau otomatis 72 jam) konfirmasi selesai, 30% langsung jadi milikmu."
            last={false}
          />
          <FlowStep
            n={4}
            icon={CalendarCheck}
            title="70% cair otomatis 7 hari kemudian"
            desc="Sisanya cair sendiri setelah masa garansi 7 hari. Nggak perlu ngapa-ngapain."
            last
          />
        </ol>
      </div>

      {/* Grafik */}
      <div className="card p-5 md:p-6" data-testid="fin-chart-card">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h2 className="font-bold text-brand-800 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-brand-500" /> Grafik 6 bulan
            </h2>
            <p className="text-sm text-brand-400">Yang sudah cair tiap bulan</p>
          </div>
        </div>
        <EarningsChart monthly={monthly} />
      </div>

      {/* Dompet & penarikan */}
      <div className="card p-5 md:p-6" data-testid="fin-wallet">
        <h2 className="font-bold text-brand-800 flex items-center gap-2">
          <Landmark className="h-5 w-5 text-brand-500" /> Dompet & Penarikan
        </h2>
        <p className="text-sm text-brand-400 mt-0.5">
          Saldo dari pencairan yang tercatat di ledger. Minimal penarikan Rp50.000.
        </p>

        <div className="rounded-2xl bg-brand-700 text-white p-4 mt-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-brand-200">Saldo bisa ditarik</p>
            <p className="text-2xl font-extrabold break-words">
              {formatRupiah(wallet?.balance ?? 0)}
            </p>
          </div>
          <Wallet className="h-8 w-8 text-brand-200 flex-shrink-0" />
        </div>

        <form onSubmit={handlePayout} className="mt-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-brand-500 mb-1 block">Nominal (Rp)</label>
              <input
                type="number"
                min={50000}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="cth. 100000"
                className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none"
                data-testid="payout-amount"
              />
            </div>
            <div>
              <label className="text-sm text-brand-500 mb-1 block">Rekening tujuan</label>
              <input
                type="text"
                value={bankAccount}
                onChange={(e) => setBankAccount(e.target.value)}
                placeholder="cth. BCA 1234567890 a.n. Nama"
                className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none"
                data-testid="payout-account"
              />
            </div>
          </div>
          {payoutErr && <p className="text-sm text-red-600">{payoutErr}</p>}
          {payoutMsg && <p className="text-sm text-success-600">{payoutMsg}</p>}
          <button
            type="submit"
            className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2"
            disabled={payoutLoading}
            data-testid="payout-submit"
          >
            <Send className="h-4 w-4" /> {payoutLoading ? "Mengirim..." : "Ajukan Penarikan"}
          </button>
        </form>

        {payouts.length > 0 && (
          <ul className="divide-y divide-brand-50 mt-4">
            {payouts.map((p) => (
              <li key={p.id} className="flex items-center justify-between py-2.5 gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-brand-800">
                    Rp{(p.amount || 0).toLocaleString("id-ID")}
                  </p>
                  <p className="text-xs text-brand-400 truncate">{p.bank_account}</p>
                </div>
                <span
                  className={`chip flex-shrink-0 ${
                    p.status === "COMPLETED"
                      ? "bg-green-100 text-green-700"
                      : p.status === "REJECTED"
                      ? "bg-red-100 text-red-600"
                      : p.status === "PROCESSING"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {p.status === "COMPLETED"
                    ? "Selesai"
                    : p.status === "REJECTED"
                    ? "Ditolak"
                    : p.status === "PROCESSING"
                    ? "Diproses"
                    : "Menunggu"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Rincian */}
      <div className="card p-5 md:p-6">
        <h2 className="font-bold text-brand-800 flex items-center gap-2 mb-3">
          <Receipt className="h-5 w-5 text-brand-500" /> Rincian
        </h2>        <dl className="divide-y divide-brand-50 text-sm">
          <div className="flex items-center justify-between py-2.5 gap-3">
            <dt className="text-brand-500">Pekerjaan selesai</dt>
            <dd className="font-extrabold text-brand-800">{s.job_count ?? 0} job</dd>
          </div>
          <div className="flex items-center justify-between py-2.5 gap-3">
            <dt className="text-brand-500">Rata-rata per job</dt>
            <dd className="font-extrabold text-brand-800">{formatRupiah(s.avg_per_job)}</dd>
          </div>
          <div className="flex items-center justify-between py-2.5 gap-3">
            <dt className="text-brand-500">
              Potongan admin
              <span className="block text-xs text-brand-300 font-normal">
                Potongan admin platform per job
              </span>
            </dt>
            <dd className="font-extrabold text-brand-800">{formatRupiah(s.platform_fee_total)}</dd>
          </div>
        </dl>
      </div>

      {/* Riwayat */}
      <div className="card p-5 md:p-6" data-testid="fin-recent">
        <h2 className="font-bold text-brand-800 flex items-center gap-2 mb-2">
          <Receipt className="h-5 w-5 text-brand-500" /> Uang masuk terakhir
        </h2>
        {recent.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-brand-400">
              Belum ada uang yang cair.
              <br />
              Selesaikan pesanan dan tunggu konfirmasi pelanggan ya.
            </p>
            <Link
              to="/worker/bookings"
              className="btn-primary inline-flex items-center gap-2 mt-4 !py-2.5"
            >
              Lihat pesanan <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-brand-50">
            {recent.map((r, i) => (
              <li key={i} className="flex items-center justify-between py-3 gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-brand-800 truncate">{r.service_name}</p>
                  <p className="text-xs text-brand-400 mt-0.5">
                    {r.released_at
                      ? new Date(r.released_at).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                  </p>
                  <span
                    className={`inline-block mt-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${
                      r.payment_status === "RELEASED"
                        ? "bg-success-50 text-success-600"
                        : "bg-blue-50 text-blue-600"
                    }`}
                  >
                    {r.payment_status === "RELEASED" ? "Lunas semua" : "Sebagian cair, sisa aman"}
                  </span>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-extrabold text-success-600">+{formatRupiah(r.amount)}</p>
                  <Link
                    to={`/worker/bookings/${r.booking_id}`}
                    className="text-xs text-brand-500 hover:text-brand-700 inline-flex items-center gap-1"
                  >
                    Detail <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Navigasi bawah khusus HP (halaman ini tanpa bottom-nav) */}
      <div className="grid grid-cols-2 gap-3 md:hidden">
        <Link to="/worker" className="btn-ghost inline-flex items-center justify-center gap-2">
          <ArrowLeft className="h-4 w-4" /> Beranda
        </Link>
        <Link
          to="/worker/bookings"
          className="btn-primary inline-flex items-center justify-center gap-2 !py-2.5"
        >
          Pesanan <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      </div>
    </div>
  );
}

function MiniCard({ icon: Icon, bg, fg, label, value, hint, testid }) {
  return (
    <div className="card p-4" data-testid={testid}>
      <span className={`inline-grid place-items-center p-2 rounded-xl ${bg}`}>
        <Icon className={`h-5 w-5 ${fg}`} />
      </span>
      <p className="text-xs font-semibold text-brand-500 mt-2">{label}</p>
      <p className="text-base md:text-lg font-extrabold text-brand-800 mt-0.5 break-words leading-snug">
        {value}
      </p>
      <p className="text-[11px] text-brand-400 mt-0.5 leading-snug">{hint}</p>
    </div>
  );
}

function FlowStep({ n, icon: Icon, title, desc, last }) {
  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center">
        <span className="h-8 w-8 rounded-full bg-brand-600 text-white grid place-items-center flex-shrink-0">
          <Icon className="h-4 w-4" />
        </span>
        {!last && <span className="w-0.5 flex-1 bg-brand-100 min-h-[12px]" />}
      </div>
      <div className={last ? "" : "pb-4"}>
        <p className="text-sm font-bold text-brand-800">
          <span className="text-brand-300 font-extrabold mr-1">{n}.</span>
          {title}
        </p>
        <p className="text-xs text-brand-500 mt-0.5 leading-relaxed">{desc}</p>
      </div>
    </li>
  );
}

/**
 * SVG bar chart tanpa library. Nilai ditulis di atas bar
 * supaya kebaca di HP tanpa hover. Muatan penuh < 260px.
 */
function EarningsChart({ monthly }) {
  const data = monthly || [];
  const max = Math.max(...data.map((d) => d.released || 0), 1);
  const chartH = 130;
  const barW = 28;
  const gap = 10;
  const padL = 34;
  const padR = 8;
  const width = padL + data.length * (barW + gap) + padR;
  const labelH = 20;
  const valueH = 16;
  const height = valueH + chartH + labelH + 8;

  return (
    <div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label="Grafik pemasukkan 6 bulan"
        data-testid="earnings-bar-chart"
      >
        {[0, 0.5, 1].map((t) => {
          const y = valueH + chartH - t * chartH;
          return (
            <g key={t}>
              <line x1={padL} y1={y} x2={width - padR} y2={y} stroke="#e8eef8" strokeWidth={1} />
              <text x={2} y={y + 3} fontSize={9} fill="#94a3b8">
                {t === 0 ? "0" : formatShort(t * max)}
              </text>
            </g>
          );
        })}

        {data.map((d, i) => {
          const x = padL + i * (barW + gap);
          const h = Math.max(((d.released || 0) / max) * chartH, d.released > 0 ? 4 : 2);
          const y = valueH + chartH - h;
          const isCurrent = i === data.length - 1;
          return (
            <g key={d.key}>
              <title>{`${d.label} ${d.year}: ${formatRupiah(d.released)} (${d.count} pencairan)`}</title>
              {d.released > 0 && (
                <text
                  x={x + barW / 2}
                  y={valueH - 4}
                  textAnchor="middle"
                  fontSize={9}
                  fontWeight={700}
                  fill={isCurrent ? "#0F3D91" : "#64748b"}
                >
                  {formatShort(d.released)}
                </text>
              )}
              <rect
                x={x}
                y={y}
                width={barW}
                height={h}
                rx={6}
                fill={isCurrent ? "#0F3D91" : "#7ba3e8"}
              />
              <text
                x={x + barW / 2}
                y={height - 4}
                textAnchor="middle"
                fontSize={10}
                fontWeight={isCurrent ? 800 : 600}
                fill={isCurrent ? "#0F3D91" : "#64748b"}
              >
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>
      {data.every((d) => !d.released) ? (
        <p className="text-center text-sm text-brand-400 pb-1">
          Belum ada pencairan di 6 bulan terakhir
        </p>
      ) : (
        <p className="text-center text-xs text-brand-300 pb-1">
          Bar biru tua = bulan berjalan
        </p>
      )}
    </div>
  );
}

function formatShort(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}jt`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}rb`;
  return String(Math.round(n));
}
